const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'figcaption',
  'figure',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  'section',
  'source',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
  'video'
]);

const GLOBAL_ATTRS = new Set(['class']);
const TAG_ATTRS = {
  a: new Set(['href', 'target', 'rel', 'title']),
  img: new Set(['src', 'alt', 'title', 'width', 'height']),
  source: new Set(['src', 'type']),
  video: new Set(['src', 'poster', 'controls', 'preload', 'muted', 'loop', 'autoplay', 'playsinline']),
  table: new Set(['border', 'cellpadding', 'cellspacing']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan'])
};

const URL_ATTRS = new Set(['href', 'src', 'poster']);
const SAFE_URL_RE = /^(https?:|mailto:|tel:|\/uploads\/|\/images\/|data:image\/(?:png|jpeg|jpg|gif|webp);base64,)/i;

const sanitizeUrl = (value = '') => {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '';
  }

  if (SAFE_URL_RE.test(normalized)) {
    return normalized;
  }

  return '';
};

const createSanitizedNode = (doc, node) => {
  if (!node) {
    return null;
  }

  if (node.nodeType === 3) {
    return doc.createTextNode(node.textContent || '');
  }

  if (node.nodeType !== 1) {
    return null;
  }

  const tagName = String(node.tagName || '').toLowerCase();
  if (!ALLOWED_TAGS.has(tagName)) {
    const fragment = doc.createDocumentFragment();
    Array.from(node.childNodes || []).forEach((childNode) => {
      const sanitizedChild = createSanitizedNode(doc, childNode);
      if (sanitizedChild) {
        fragment.appendChild(sanitizedChild);
      }
    });
    return fragment;
  }

  const safeNode = doc.createElement(tagName);
  const allowedAttrs = TAG_ATTRS[tagName] || new Set();

  Array.from(node.attributes || []).forEach((attribute) => {
    const attrName = String(attribute.name || '').toLowerCase();
    if (!GLOBAL_ATTRS.has(attrName) && !allowedAttrs.has(attrName)) {
      return;
    }

    const attrValue = String(attribute.value || '').trim();
    if (!attrValue) {
      return;
    }

    if (URL_ATTRS.has(attrName)) {
      const safeValue = sanitizeUrl(attrValue);
      if (!safeValue) {
        return;
      }
      safeNode.setAttribute(attrName, safeValue);
      return;
    }

    safeNode.setAttribute(attrName, attrValue);
  });

  if (tagName === 'a') {
    if (safeNode.getAttribute('target') === '_blank') {
      safeNode.setAttribute('rel', 'noopener noreferrer');
    }

    if (!safeNode.getAttribute('href')) {
      safeNode.removeAttribute('target');
      safeNode.removeAttribute('rel');
    }
  }

  if (tagName === 'video' && !safeNode.getAttribute('controls')) {
    safeNode.setAttribute('controls', 'controls');
  }

  Array.from(node.childNodes || []).forEach((childNode) => {
    const sanitizedChild = createSanitizedNode(doc, childNode);
    if (sanitizedChild) {
      safeNode.appendChild(sanitizedChild);
    }
  });

  return safeNode;
};

export const sanitizeHtml = (input = '') => {
  const source = String(input || '').trim();
  if (!source) {
    return '';
  }

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return source;
  }

  const parsed = new DOMParser().parseFromString(source, 'text/html');
  const safeDocument = document.implementation.createHTMLDocument('');
  const container = safeDocument.createElement('div');

  Array.from(parsed.body.childNodes || []).forEach((childNode) => {
    const sanitizedChild = createSanitizedNode(safeDocument, childNode);
    if (sanitizedChild) {
      container.appendChild(sanitizedChild);
    }
  });

  return container.innerHTML;
};

export default sanitizeHtml;
